/**
 * Making what a scene needs. Every need looks first and makes only what is
 * missing, so running one twice is the same as running it once; this adds the
 * retry, and the wait for the world to show what was made.
 */

/** The tour's chat session: the kernel's own need, by this name in a scene. */
export const SESSION_NEED = "session";

export const ATTEMPTS = 3;
export const BACKOFF_MS: readonly number[] = [600, 1500];
/** How long a made thing may take to show up in the world. */
const SHOW_MS = 8000;
const POLL_MS = 50;

export class NeedFailed extends Error {
  constructor(
    readonly need: string,
    readonly cause?: unknown,
  ) {
    super(`tour need failed: ${need}`);
  }
}

/** The scene moved on while a need was being made. */
export class Cancelled extends Error {}

export interface Signal {
  cancelled: boolean;
}

export const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/** Resolves once `condition` holds; false when it has not after `timeoutMs`. */
export async function until(
  condition: () => boolean,
  signal: Signal,
  timeoutMs: number,
  wait: (ms: number) => Promise<void> = sleep,
): Promise<boolean> {
  for (let waited = 0; ; waited += POLL_MS) {
    if (signal.cancelled) throw new Cancelled();
    if (condition()) return true;
    if (waited >= timeoutMs) return false;
    await wait(POLL_MS);
  }
}

/** One tab at a time makes a thing, so two tabs on one tour make it once. */
async function locked<T>(name: string, run: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks) return run();
  return locks.request(`co-tour:${name}`, run);
}

export async function ensureNeed({
  name,
  met,
  ensure,
  signal,
  attempts = ATTEMPTS,
  backoff = BACKOFF_MS,
  wait = sleep,
}: {
  name: string;
  met: () => boolean;
  ensure: () => Promise<void>;
  signal: Signal;
  attempts?: number;
  backoff?: readonly number[];
  wait?: (ms: number) => Promise<void>;
}): Promise<void> {
  let cause: unknown;
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (signal.cancelled) throw new Cancelled();
    if (met()) return;
    try {
      await locked(name, async () => {
        if (!met()) await ensure();
      });
      if (await until(met, signal, SHOW_MS, wait)) return;
      cause = new Error("made, but never showed");
    } catch (err) {
      if (err instanceof Cancelled) throw err;
      cause = err;
    }
    if (attempt < attempts - 1)
      await wait(backoff[Math.min(attempt, backoff.length - 1)] ?? 0);
  }
  if (signal.cancelled) throw new Cancelled();
  if (met()) return;
  throw new NeedFailed(name, cause);
}
